import { useState } from "react";
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { useUsers, useCreateUser, useUpdateUser } from "@/api/users";
import { usePagination } from "@/hooks/use-pagination";
import { DataTable } from "@/components/data-table/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import type { User } from "@/api/types";

const roleColors: Record<string, "danger" | "accent"> = {
  super_admin: "danger",
  operator: "accent",
};

export function UsersAdminPage() {
  const { page, pageSize, search, params, setPage, setPageSize, setSearch } = usePagination();
  const { data, isLoading } = useUsers(params);
  const [dialogOpen, setDialogOpen] = useState(false);

  const columns: ColumnDef<User, unknown>[] = [
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => (
        <Link to={`/admin/users/${row.original.id}`} className="text-accent hover:underline">
          {row.original.email}
        </Link>
      ),
    },
    { accessorKey: "first_name", header: "First Name" },
    { accessorKey: "last_name", header: "Last Name" },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ row }) => <Badge variant={roleColors[row.original.role]}>{row.original.role.replace("_", " ")}</Badge>,
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => <Badge variant={row.original.status === "active" ? "success" : "danger"}>{row.original.status}</Badge>,
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Users</h1>
        <Button onClick={() => setDialogOpen(true)}>Create User</Button>
      </div>
      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search users..."
        page={page}
        pageSize={pageSize}
        totalPages={data?.meta?.totalPages ?? 1}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        total={data?.meta?.total}
      />
      <CreateUserDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}

function CreateUserDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createUser = useCreateUser();
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", role: "operator", password: "" });

  const fieldErrors = createUser.isError
    ? (createUser.error as { response?: { data?: { error?: { details?: Record<string, string[]> } } } })
        ?.response?.data?.error?.details ?? {}
    : {};
  const generalError = createUser.isError && Object.keys(fieldErrors).length === 0
    ? (createUser.error as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message || "Something went wrong. Please try again."
    : null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createUser.reset();
    createUser.mutate(form, { onSuccess: () => { onClose(); setForm({ email: "", first_name: "", last_name: "", role: "operator", password: "" }); } });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Create User</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Email</label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          {fieldErrors.email && <p className="mt-1 text-xs text-danger">{fieldErrors.email[0]}</p>}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">First Name</label>
            <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            {fieldErrors.first_name && <p className="mt-1 text-xs text-danger">{fieldErrors.first_name[0]}</p>}
          </div>
          <div>
            <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Last Name</label>
            <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            {fieldErrors.last_name && <p className="mt-1 text-xs text-danger">{fieldErrors.last_name[0]}</p>}
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Role</label>
          <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="operator">Operator</option>
            <option value="super_admin">Super Admin</option>
          </Select>
          {fieldErrors.role && <p className="mt-1 text-xs text-danger">{fieldErrors.role[0]}</p>}
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Password</label>
          <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} />
          {fieldErrors.password && <p className="mt-1 text-xs text-danger">{fieldErrors.password[0]}</p>}
        </div>
        {generalError && <p className="text-sm text-danger">{generalError}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={createUser.isPending}>{createUser.isPending ? "Creating..." : "Create"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
